import type { PlatformMailboxCategory, PlatformMailboxView, PlatformMailDomainKind } from "@/api";

/**
 * Pure decisions behind the service-mailbox screen (#1214). No React, no API: what the console
 * may offer for a row, how a category reads, and what a form must contain before it is sent.
 *
 * Offering a control is not the enforcement — the server answers 409 whatever the browser shows
 * — but a button that can only ever fail is a lie, so the two are kept in step here and pinned
 * by tests.
 */

export type PlatformMailboxAction = "open" | "adopt" | "reset" | "delete";

/**
 * Service rows may be opened, reset and deleted; an unmanaged account may only be adopted; the
 * rest read-only. Opening is offered first because it is the one thing done daily (#1235), and
 * only for `service`: the console has no business sending anyone into a business's mailbox, and
 * the server answers 409 whatever the browser shows.
 *
 * A work mailbox is never opened from here (#1899). Its password belongs to its person, and
 * opening rotates the account's only credential, which would sign them out of every mail app.
 */
export function actionsFor(category: PlatformMailboxCategory, domain: PlatformMailDomainKind): PlatformMailboxAction[] {
  switch (category) {
    case "service": return domain === "work" ? ["reset", "delete"] : ["open", "reset", "delete"];
    case "unmanaged": return ["adopt"];
    case "tenant":
    case "system":
      return [];
  }
}

export type CategoryTone = "success" | "info" | "neutral" | "warning";

export function categoryTone(category: PlatformMailboxCategory): CategoryTone {
  switch (category) {
    case "service": return "success";
    case "tenant": return "info";
    case "system": return "neutral";
    case "unmanaged": return "warning";
  }
}

const CATEGORY_ORDER: Record<PlatformMailboxCategory, number> = {
  service: 0,
  unmanaged: 1,
  tenant: 2,
  system: 3,
};

const DOMAIN_ORDER: Record<PlatformMailDomainKind, number> = { work: 0, platform: 1 };

/**
 * What the owner can act on first, then what they might adopt, then what is only shown. Within
 * that, the company's work addresses before the platform's service addresses (#1899).
 */
export function sortPlatformMailboxes(rows: PlatformMailboxView[]): PlatformMailboxView[] {
  return [...rows].sort((a, b) =>
    CATEGORY_ORDER[a.category] - CATEGORY_ORDER[b.category]
      || DOMAIN_ORDER[a.domain] - DOMAIN_ORDER[b.domain]
      || a.localPart.localeCompare(b.localPart),
  );
}

export interface PlatformMailboxForm {
  localPart: string;
  description: string;
  domain: PlatformMailDomainKind;
}

/**
 * A fresh form. Where the installation has a work domain, a new mailbox is a work address unless
 * the owner picks the platform domain (#1899): those are the addresses people are made for now.
 */
export function emptyPlatformMailboxForm(domain: PlatformMailDomainKind = "platform"): PlatformMailboxForm {
  return { localPart: "", description: "", domain };
}

/**
 * Only what the browser can know: an empty name has nothing to send. Everything about the shape
 * of the name — length, charset, the reserved list that does NOT apply here — is the server's
 * verdict, shown as it comes back (`web/app` renders server facts, it does not own the rules).
 */
export function platformMailboxFormErrors(form: PlatformMailboxForm): string[] {
  return form.localPart.trim() ? [] : ["platformMailbox.error.localPartRequired"];
}

export function buildPlatformMailboxPayload(
  form: PlatformMailboxForm,
): { localPart: string; description: string; domain: PlatformMailDomainKind } {
  return { localPart: form.localPart.trim().toLowerCase(), description: form.description.trim(), domain: form.domain };
}
